import React, { useState, useCallback, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FieldError, svgSprite } from '../../../../hybrid/wrappers';
import { useDropzone } from 'react-dropzone';
import UserContext, { UserProfileContext } from '../../../../common/utils/getContext';
import T, { t } from '../../../../common/components/T';
import FromInputTitle from '../FormFieldTitle/FormFieldTitle';
import { useWidgetTranslates } from '../../../../common/utils/utils';
import Loader from '../../../plug/Loader/Loader';
import { getFilesThumbs, getThumbs, onDropFunction, useDownload, useProgress } from './helpers';
import { useWindowDimensions } from '../../../../common/utils/windowDimensions';
import css from './FormDropzone.module.scss';
import classnames from 'classnames';
import { generateCompStylers } from '../../../../common/utils/styles';

const FormInputDropzone = ({
    attribute,
    max = 999,
    maxScale = 4000,
    title,
    button,
    hint,
    compStyle,
    setFormData,
    value,
    multiple = true,
    noDrag,
    previewTitle,
    deleteBtn = true,
    infoFile,
    accept = 'image/jpeg, image/jpg, image/png, .heic, .heif',
    downloading = false,
    exportData = {},
    required,
    errors,
    deleteKey,
    adminVerification
}) => {
    const { stylers, classes } = generateCompStylers(compStyle);
    const { officePage = false } = useContext(UserProfileContext) || {};
    const { setAlertData } = useContext(UserContext);

    const screenWidth = useWindowDimensions() < 768;

    const [loader, setLoader] = useState(true);

    const { setConvertProgress, Bar } = useProgress();
    const downloadFile = useDownload(multiple, exportData);
    const transl = useWidgetTranslates('form');

    const nameForm = screenWidth
        ? t('FormInputDropzone-InfoFile1-mob', 'Нажмите для загрузки файла', 'widget', transl, 'form')
        : !infoFile
        ? t(
              'FormInputDropzone-InfoFile1',
              'Перетащите изображение в это поле или воспользуйтесь кнопкой «Загрузить»',
              'widget',
              transl,
              'form'
          )
        : t(
              'FormInputDropzone-InfoFile2',
              'Перетащите файл в это поле или воспользуйтесь кнопкой «Загрузить»',
              'widget',
              transl,
              'form'
          );

    const [val, setVal] = useState(value || multiple ? [] : null);
    const { getRootProps, getInputProps, fileRejections } = useDropzone({
        noDrag: !!noDrag,
        accept: accept,
        onDrop: onDropFunction(
            attribute,
            multiple,
            val,
            maxScale,
            max,
            setVal,
            setFormData,
            transl,
            setAlertData,
            setLoader,
            setConvertProgress
        )
    });
    useEffect(() => {
        if (fileRejections.length && fileRejections[0].errors?.length) {
            if (fileRejections[0].errors[0].code === 'file-invalid-type') {
                setAlertData({
                    type: 'error',
                    cont: `${t(
                        'FormInputDropzone-PermissibleFileExtension',
                        'Допустимое расширение файлов',
                        'widget',
                        transl,
                        'form'
                    )} - ${accept}`
                });
            } else {
                setAlertData({
                    type: 'error',
                    cont: t('FormInputDropzone-LoadingFileError', 'Ошибка загрузки файлов', 'widget', transl, 'form')
                });
            }
        }
    }, [accept, fileRejections, setAlertData, transl]);
    const [deletedImages, setDeletedImages] = useState([]);
    const deleteFunc = useCallback(
        (file, index, e) => {
            e.preventDefault();
            e.stopPropagation();
            let newValue = multiple
                ? val.filter((item, idx) => {
                      if (file.url) {
                          return item?.url !== file?.url;
                      }
                      if (item?.id || item?.id === 0) {
                          return `${item?.id}` !== `${file?.id}`;
                      }
                      return `${index}` !== `${idx}`;
                  })
                : null;
            setVal(newValue);
            setFormData(attribute, newValue);

            const newDeletedImages = [...deletedImages, file];
            setDeletedImages(newDeletedImages);
            setFormData(deleteKey || 'deletedImages', newDeletedImages);
        },
        [attribute, deletedImages, multiple, setFormData, val, deleteKey]
    );

    const thumbs = useMemo(
        () =>
            infoFile
                ? getFilesThumbs(val, hint, downloading, deleteFunc, downloadFile, transl, compStyle, multiple, exportData)
                : getThumbs(val, deleteBtn, deleteFunc, downloading, downloadFile, noDrag, css, compStyle),
        [compStyle, deleteBtn, deleteFunc, downloadFile, downloading, exportData, hint, infoFile, multiple, noDrag, transl, val]
    );

    useEffect(() => {
        setVal(multiple ? value || [] : value || null);
    }, [multiple, value]);

    return (
        <div style={stylers.wrapper} className={classnames(css.formGroup, css['separated_title'], classes.wrapper)}>
            {!!title && (
                <FromInputTitle
                    id={`input_${attribute}`}
                    errors={errors}
                    title={title}
                    compStyle={compStyle}
                    hint={title?.hint}
                    required={required}
                    variationStyle={'separated_title'}
                />
            )}
            <div style={stylers.inner} className={classnames(css.formDropzoneInner, classes.inner)}>
                {!noDrag && (
                    <div
                        style={stylers.loadDrop}
                        {...getRootProps({
                            className: classnames(
                                adminVerification ? css.formDropzoneLoadDropCustom : css.formDropzoneLoadDrop,
                                classes.loadDrop,
                                'dropzone'
                            )
                        })}
                    >
                        <input {...(!downloading && getInputProps())} />
                        <div style={stylers.imageGap} className={classes.imageGap}>
                            {svgSprite(
                                officePage ? 'plug-load-image-office' : infoFile ? 'plug-load-file-image' : 'plug-load-image',
                                {
                                    width: officePage ? '48px' : '72px',
                                    height: officePage ? '48px' : '72px'
                                }
                            )}
                        </div>
                        <div style={{ width: '100%' }}>
                            {loader === 'heic' ? (
                                <>
                                    <div
                                        style={{
                                            maxWidth: '400px',
                                            width: '100%',
                                            margin: '0 auto'
                                        }}
                                    >
                                        <Loader compStyles={{ loader: css.loader }} /> <Bar />
                                    </div>
                                    {t('FormInputDropzone-convert', 'Конвертация файла в формат jpeg', 'widget', transl, 'form')}
                                </>
                            ) : (
                                nameForm
                            )}
                        </div>
                    </div>
                )}
                {val?.length || (!multiple && val) ? (
                    <div
                        style={stylers.previewInner}
                        className={classnames(!noDrag && css.formDropzoneLoadImageContainerPreview, classes.previewInner)}
                    >
                        {previewTitle && (
                            <p style={stylers.previewTitle} className={classes.previewTitle}>
                                {previewTitle}
                            </p>
                        )}
                        {thumbs}
                    </div>
                ) : null}
                <div
                    style={stylers.formLoadLabelWrap}
                    className={classnames(css.formDropzoneLoadLabelWrap, classes.formLoadLabelWrap)}
                >
                    <p
                        style={stylers.button}
                        {...getRootProps({
                            className: classnames(!noDrag && css.formDropzoneLoadLabel, classes.button)
                        })}
                    >
                        <input {...(!downloading && getInputProps())} />
                        {button?.icon !== false
                            ? svgSprite(button?.icon || 'painting', {
                                  width: '18px',
                                  height: '18px',
                                  className: classnames(css.formDropzoneLoadLabelIcon, classes.buttonIcon),
                                  style: stylers.buttonIcon
                              })
                            : null}
                        {button?.body || <T textName='upload' defaultStr='Загрузить' page='all' />}
                    </p>
                    {hint && (
                        <div style={stylers.formImageText} className={classnames(css.formDropzoneText, classes.formImageText)}>
                            {hint}
                        </div>
                    )}
                </div>
            </div>
            {errors && errors.length
                ? errors.map((err, idx) => (
                      <FieldError compStyle={compStyle?.error} key={`${attribute}${idx}`}>
                          {err}
                      </FieldError>
                  ))
                : null}
        </div>
    );
};
FormInputDropzone.propTypes = {
    attribute: PropTypes.string,
    max: PropTypes.number,
    maxScale: PropTypes.number,
    title: PropTypes.object,
    button: PropTypes.object,
    hint: PropTypes.node,
    compStyle: PropTypes.object,
    setFormData: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    multiple: PropTypes.bool,
    noDrag: PropTypes.bool,
    previewTitle: PropTypes.node,
    deleteBtn: PropTypes.bool,
    infoFile: PropTypes.bool,
    accept: PropTypes.string,
    downloading: PropTypes.bool,
    exportData: PropTypes.object,
    required: PropTypes.bool,
    errors: PropTypes.array
};
export default FormInputDropzone;
